import { useState, useEffect, useMemo } from 'react';
import { useApp } from '@contexts/AppContext';
import { useAuth } from '@contexts/AuthContext';
import { DEMO_CUSTOMER_ACCOUNTS } from '@data/demoData';
import salesAreasData from '@data/salesAreas.json';
import salesEntitiesData from '@data/salesEntities.json';

/**
 * AccountSwitcher (A5d) — Sub-Plan 4 bonus rewire.
 *
 * Employee: cascading editable dropdowns (Sales Area → Sales Entity → Customer Name).
 *   Sales Area + Sales Entity now read from `src/data/salesAreas.json` +
 *   `src/data/salesEntities.json` (built from the canonical Excel by
 *   `scripts/build_sales_entity_data.py`).
 *   ON ENTITY CHANGE: dispatches the 2-letter `entityCode` (e.g. 'SE', 'FN',
 *   'NO', 'US') to AppContext.state.salesEntity — NOT the placeholder ids
 *   from the previous implementation. `useAvailability` reads this value
 *   directly as its `entityCode`.
 *
 *   Customer dropdown populated from DEMO_CUSTOMER_ACCOUNTS keyed by
 *   entityCode. In production this will be fetched from SAP Customer Master
 *   via the MRT backend API.
 *
 * Customer: read-only single account with checkmark (unchanged).
 *
 * Mockup ref: #16 (employee drawer — account context switcher)
 */

// Build {area -> [entities]} index from the JSON. Filter to active+displayed
// entries for the production list.
function buildAreaIndex() {
  const areas = (salesAreasData.areas || [])
    .filter((a) => a.isActive && a.isDisplayed)
    .map((a) => ({ ...a, entities: [] }));
  const byId = new Map(areas.map((a) => [a.id, a]));

  for (const e of salesEntitiesData.entities || []) {
    if (!e.isActive || !e.isDisplayed || !e.entityCode) continue;
    const bucket = byId.get(e.areaId);
    if (bucket) {
      bucket.entities.push(e);
    }
  }

  // Sort entities by displayName for stable UX
  for (const a of areas) {
    a.entities.sort((x, y) =>
      String(x.displayName || x.entityCode).localeCompare(
        String(y.displayName || y.entityCode)
      )
    );
  }
  // Drop empty areas — nothing for the user to pick
  return areas.filter((a) => a.entities.length > 0);
}

export default function AccountSwitcher({ salesAreaData }) {
  const { state, dispatch } = useApp();
  const { userType, account } = useAuth();

  const areas = useMemo(() => salesAreaData || buildAreaIndex(), [salesAreaData]);

  // Find the area that owns the currently-selected entityCode (so when geo
  // detection writes just an entity code, the area dropdown auto-syncs).
  const initialArea = useMemo(() => {
    if (state.salesArea) return state.salesArea;
    if (state.salesEntity) {
      const owner = areas.find((a) =>
        a.entities.some((e) => e.entityCode === state.salesEntity)
      );
      if (owner) return owner.id;
    }
    return '';
  }, [areas, state.salesArea, state.salesEntity]);

  const [selectedArea, setSelectedArea] = useState(initialArea);
  const [selectedEntity, setSelectedEntity] = useState(state.salesEntity || '');
  const [selectedCustomer, setSelectedCustomer] = useState(
    state.customerAccount || ''
  );

  // Keep local UI in sync if AppContext salesEntity is set externally (e.g.
  // by useRegion IP-detection after first paint).
  useEffect(() => {
    if (state.salesEntity && state.salesEntity !== selectedEntity) {
      setSelectedEntity(state.salesEntity);
      const owner = areas.find((a) =>
        a.entities.some((e) => e.entityCode === state.salesEntity)
      );
      if (owner && owner.id !== selectedArea) setSelectedArea(owner.id);
    }
  }, [state.salesEntity, areas]);

  const currentArea = areas.find((a) => a.id === selectedArea);
  const entities = currentArea?.entities || [];

  // Customer accounts for the selected entity
  const customers = useMemo(
    () => DEMO_CUSTOMER_ACCOUNTS[selectedEntity] || [],
    [selectedEntity]
  );

  // Cascading resets
  useEffect(() => {
    if (selectedEntity && !entities.some((e) => e.entityCode === selectedEntity)) {
      setSelectedEntity('');
      setSelectedCustomer('');
    }
  }, [selectedArea]);

  useEffect(() => {
    setSelectedCustomer('');
  }, [selectedEntity]);

  // Sync to AppContext — only dispatch when the user actually changes the
  // value, to avoid re-dispatch loops with useRegion's geo write.
  useEffect(() => {
    if ((state.salesArea || '') !== (selectedArea || '')) {
      dispatch({ type: 'SET_SALES_AREA', payload: selectedArea || null });
    }
  }, [selectedArea]);

  useEffect(() => {
    if ((state.salesEntity || '') !== (selectedEntity || '')) {
      dispatch({ type: 'SET_SALES_ENTITY', payload: selectedEntity || null });
    }
  }, [selectedEntity]);

  useEffect(() => {
    if ((state.customerAccount || '') !== (selectedCustomer || '')) {
      dispatch({
        type: 'SET_CUSTOMER_ACCOUNT',
        payload: selectedCustomer || null,
      });
    }
  }, [selectedCustomer]);

  // Employee view: editable cascading dropdowns
  if (userType === 'employee') {
    return (
      <div className="space-y-3" data-testid="account-switcher-employee">
        <p className="text-xs text-n5 font-sandvik-text tracking-wide font-normal">
          Account context
        </p>

        {/* Sales Area */}
        <div>
          <label className="text-xs text-n5 font-sandvik-text mb-1 block">
            Sales area
          </label>
          <select
            data-testid="sales-area-select"
            value={selectedArea}
            onChange={(e) => setSelectedArea(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-n2 rounded-none font-sandvik-text text-carbon-black
                       focus:outline-none focus:border-electric-blue bg-white"
          >
            <option value="">Select sales area</option>
            {areas.map((area) => (
              <option key={area.id} value={area.id}>
                {area.name}
              </option>
            ))}
          </select>
        </div>

        {/* Sales Entity */}
        <div>
          <label className="text-xs text-n5 font-sandvik-text mb-1 block">
            Sales entity
          </label>
          <select
            data-testid="sales-entity-select"
            value={selectedEntity}
            onChange={(e) => setSelectedEntity(e.target.value)}
            disabled={!selectedArea}
            className="w-full px-3 py-2 text-sm border border-n2 rounded-none font-sandvik-text text-carbon-black
                       focus:outline-none focus:border-electric-blue bg-white disabled:bg-n1 disabled:text-n4"
          >
            <option value="">Select entity</option>
            {entities.map((entity) => (
              <option key={entity.entityCode} value={entity.entityCode}>
                {entity.displayName || entity.entityCode}
              </option>
            ))}
          </select>
        </div>

        {/* Customer Name */}
        <div>
          <label className="text-xs text-n5 font-sandvik-text mb-1 block">
            Customer
          </label>
          <select
            data-testid="customer-select"
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
            disabled={!selectedEntity}
            className="w-full px-3 py-2 text-sm border border-n2 rounded-none font-sandvik-text text-carbon-black
                       focus:outline-none focus:border-electric-blue bg-white disabled:bg-n1 disabled:text-n4"
          >
            <option value="">
              {!selectedEntity
                ? 'Select entity first'
                : customers.length === 0
                  ? 'No customers for this entity'
                  : 'Select customer'}
            </option>
            {customers.map((c) => (
              <option key={c.id} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
          {selectedCustomer && (
            <p className="text-xs text-sandvik-success mt-1 flex items-center gap-1">
              <span className="material-symbols-outlined text-sm">check_circle</span>
              Acting as {selectedCustomer.split(' ').slice(1).join(' ')}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Customer view: user name + read-only single account with checkmark
  if (userType === 'customer' || userType === 'registered') {
    return (
      <div className="space-y-3">
        {/* User identity */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-electric-blue text-white text-sm font-normal rounded-full flex items-center justify-center font-sandvik-text flex-shrink-0">
            {(() => {
              const n = account?.name || '';
              const parts = n.split(' ');
              if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
              return n.charAt(0).toUpperCase() || '?';
            })()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-normal font-sandvik-text text-carbon-black truncate">
              {account?.name || 'User'}
            </p>
            <p className="text-xs text-n5 font-sandvik-text truncate">
              {account?.company || account?.customerName || ''}
            </p>
          </div>
        </div>

        {/* Account card */}
        <div>
          <p className="text-xs text-n5 font-sandvik-text tracking-wide font-normal mb-2">
            Your account
          </p>
          <div className="flex items-center gap-3 p-3 border border-n2 bg-n1">
            <span className="material-symbols-outlined text-electric-blue text-lg">
              check_circle
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-normal font-sandvik-text text-carbon-black truncate">
                {account?.customerId || ''} {account?.company || account?.customerName || account?.name || 'Your company'}
              </p>
              {account?.salesEntityDisplay && (
                <p className="text-xs text-n5 font-sandvik-text">
                  {account.salesEntityDisplay}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Guest: nothing
  return null;
}
